import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Users, Vote, Clock, LogIn, User, LogOut, ShoppingBag } from 'lucide-react';
import VoteModal from './VoteModal';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVoteModalOpen, setIsVoteModalOpen] = useState(false);
  const [serverStatus, setServerStatus] = useState({ online: false, players: 0, startTime: '' });
  const [uptime, setUptime] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  // Check login status
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('authToken');
      const storedUsername = localStorage.getItem('username');
      setIsLoggedIn(!!token && !!storedUsername);
      setUsername(storedUsername || '');
    };

    checkAuth();
    // Listen for storage changes (login/logout from other tabs)
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, [location]); // Re-check on route change

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    setIsLoggedIn(false);
    setUsername('');
    navigate('/login');
  };

  // Calculate uptime from startTime
  const calculateUptime = (startTimeStr: string) => {
    if (!startTimeStr) return '';
    try {
      const startTime = new Date(startTimeStr);
      const now = new Date();
      const diffMs = now.getTime() - startTime.getTime();

      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) return `${days}d ${hours}h`;
      if (hours > 0) return `${hours}h ${minutes}m`;
      return `${minutes}m`;
    } catch {
      return '';
    }
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch server status on mount and every 5 minutes
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await fetch('/api/status');
        const data = await response.json();
        setServerStatus(data);
        if (data.startTime) {
          setUptime(calculateUptime(data.startTime));
        }
      } catch (error) {
        setServerStatus({ online: false, players: 0, startTime: '' });
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5 * 60 * 1000); // 5 minutes

    // Update uptime every minute
    const uptimeInterval = setInterval(() => {
      if (serverStatus.startTime) {
        setUptime(calculateUptime(serverStatus.startTime));
      }
    }, 60 * 1000);

    return () => {
      clearInterval(interval);
      clearInterval(uptimeInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/download', label: 'Download' },
    { path: '/rankings', label: 'Rankings' },
    { path: '/skills', label: 'Skills' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className={`sticky top-0 z-50 border-b border-daruma-red/30 transition-all duration-200 ${
      isScrolled
        ? 'bg-daruma-ink shadow-lg shadow-black/40'
        : 'bg-daruma-ink/95 backdrop-blur-sm'
    }`}>
      {/* Red left-edge stripe */}
      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-daruma-red" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between transition-all duration-200 ${isScrolled ? 'h-13' : 'h-15'}`} style={{ height: isScrolled ? '52px' : '60px' }}>
          {/* Left - Logo */}
          <Link to="/" className="flex items-center gap-2 group pl-2">
            <img src="/assets/logo.png" alt="logo" className="w-8 h-8 object-contain logo-icon transition-transform duration-300" />
            <span className="text-base font-black text-white uppercase tracking-wider group-hover:text-daruma-red transition-colors duration-200">
              {process.env.REACT_APP_SERVER_NAME}
            </span>
          </Link>

          {/* Center - Navigation Links */}
          <div className="hidden md:flex items-center absolute left-1/2 transform -translate-x-1/2">
            <div className="flex items-center gap-0">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-5 py-2 text-xs font-bold uppercase tracking-widest transition-all duration-200 border-b-2 ${
                    isActive(link.path)
                      ? 'text-daruma-red border-daruma-red'
                      : 'text-gray-400 border-transparent hover:text-white hover:border-white/30'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={() => setIsVoteModalOpen(true)}
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-gray-400 border-b-2 border-transparent hover:text-white hover:border-white/30 transition-all duration-200 flex items-center gap-1.5"
              >
                <Vote className="w-3 h-3" />
                Vote
              </button>
              <a
                href="https://ko-fi.com/mapledaruma"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-gray-400 border-b-2 border-transparent hover:text-white hover:border-white/30 transition-all duration-200 flex items-center gap-1.5"
              >
                <ShoppingBag className="w-3 h-3" />
                Donate
              </a>
              {isLoggedIn ? (
                <Link
                  to="/account"
                  className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-gray-400 border-b-2 border-transparent hover:text-white hover:border-white/30 transition-all duration-200 flex items-center gap-1.5"
                >
                  <User className="w-3 h-3" />
                  {username}
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="ml-2 px-4 py-1.5 bg-daruma-red hover:bg-daruma-crimson text-white text-xs font-bold uppercase tracking-widest transition-colors duration-200 flex items-center gap-1.5"
                >
                  <LogIn className="w-3 h-3" />
                  Login
                </Link>
              )}
            </div>
          </div>

          {/* Right - Server Status */}
          <div className="hidden md:flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 border border-white/10 bg-white/5">
              <span className="relative flex h-2 w-2">
                {serverStatus.online ? (
                  <>
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </>
                ) : (
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-daruma-red"></span>
                )}
              </span>
              <span className={`text-xs font-bold uppercase tracking-wider ${serverStatus.online ? 'text-green-400' : 'text-daruma-red'}`}>
                {serverStatus.online ? 'Online' : 'Offline'}
              </span>
              {serverStatus.online && uptime && (
                <>
                  <div className="w-px h-3 bg-white/20"></div>
                  <Clock className="w-3 h-3 text-gray-500" />
                  <span className="text-xs text-gray-400">{uptime}</span>
                </>
              )}
              <div className="w-px h-3 bg-white/20"></div>
              <Users className="w-3 h-3 text-gray-500" />
              <span className="text-xs text-gray-400">{serverStatus.players.toLocaleString()}</span>
            </div>
          </div>

          {/* Mobile - Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-2 py-1 border border-white/10 bg-white/5">
              <span className={`w-1.5 h-1.5 ${serverStatus.online ? 'bg-green-500 animate-pulse' : 'bg-daruma-red'} rounded-full`}></span>
              <span className="text-xs font-medium text-gray-400">
                {serverStatus.players >= 1000 ? `${(serverStatus.players / 1000).toFixed(1)}k` : serverStatus.players}
              </span>
            </div>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-3 border-t border-white/10">
            <div className="flex flex-col gap-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-3 text-xs font-bold uppercase tracking-widest transition-all duration-200 border-l-2 ${
                    isActive(link.path)
                      ? 'border-daruma-red text-daruma-red bg-daruma-red/10'
                      : 'border-transparent text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={() => { setIsOpen(false); setIsVoteModalOpen(true); }}
                className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-gray-400 border-l-2 border-transparent hover:text-white hover:bg-white/5 transition-all duration-200 flex items-center gap-2 text-left"
              >
                <Vote className="w-3.5 h-3.5" />
                Vote
              </button>
              <a
                href="https://ko-fi.com/mapledaruma"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
                className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-gray-400 border-l-2 border-transparent hover:text-white hover:bg-white/5 transition-all duration-200 flex items-center gap-2"
              >
                <ShoppingBag className="w-3.5 h-3.5" />
                Shop
              </a>
              <div className="border-t border-white/10 my-2" />
              {isLoggedIn ? (
                <>
                  <Link
                    to="/account"
                    onClick={() => setIsOpen(false)}
                    className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-gray-400 border-l-2 border-transparent hover:text-white hover:bg-white/5 transition-all duration-200 flex items-center gap-2"
                  >
                    <User className="w-3.5 h-3.5" />
                    {username}
                  </Link>
                  <button
                    onClick={() => { setIsOpen(false); handleLogout(); }}
                    className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-daruma-red border-l-2 border-transparent hover:bg-daruma-red/10 transition-all duration-200 flex items-center gap-2 text-left w-full"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setIsOpen(false)}
                  className="mx-4 mt-1 py-2.5 bg-daruma-red text-white text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-daruma-crimson transition-colors"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Login / Register
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Vote Modal */}
      <VoteModal isOpen={isVoteModalOpen} onClose={() => setIsVoteModalOpen(false)} />
    </nav>
  );
};

export default Navbar;
